/**
 * What is wrong with a typed reset code, before it is sent. D29.
 *
 * The API answers one refusal for every bad code, so that a code never
 * confirms its own shape. The shape is no secret, though: every reset email
 * prints it. A code is 16 random bytes in Crockford base 32, 26 symbols, then
 * one check symbol, the value mod 37, 27 in all, mailed in groups of four.
 * This mirrors `veri-plus-backend/src/utils/crockford.ts` (`normalize`,
 * `checkSymbol`, `stripCheckSymbol`) and `normalizeTypedToken` in `tokens.ts`,
 * so a code it passes is one the API would look up, and one it refuses the API
 * would refuse without looking.
 */

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CHECK_ALPHABET = `${ALPHABET}*~$=U`;

/** Symbols in a whole code, the check symbol included. */
export const RESET_CODE_LENGTH = 27;

/** The shape the email prints, for the placeholder. */
export const RESET_CODE_SHAPE = 'XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXX';

const DECODE = new Map<string, number>([...ALPHABET].map((c, i) => [c, i]));
// The confusables the backend folds.
DECODE.set('O', 0);
DECODE.set('I', 1);
DECODE.set('L', 1);

export type ResetCodeFault =
  | { kind: 'empty' }
  | { kind: 'character'; character: string }
  | { kind: 'short'; length: number }
  | { kind: 'long'; length: number }
  | { kind: 'typo' };

/** Null when the code could be a real one. */
export function resetCodeFault(typed: string): ResetCodeFault | null {
  const trimmed = typed.trim();
  // The 96-hex form these secrets had before, which the API still accepts.
  if (/^[0-9a-f]{96}$/i.test(trimmed)) return null;
  const cleaned = trimmed.toUpperCase().replace(/[\s-]/g, '');
  if (cleaned === '') return { kind: 'empty' };

  const body = cleaned.slice(0, -1);
  const last = cleaned.slice(-1);
  const stray = [...body].find((c) => !DECODE.has(c));
  if (stray !== undefined) return { kind: 'character', character: stray };
  if (!DECODE.has(last) && !CHECK_ALPHABET.includes(last))
    return { kind: 'character', character: last };

  if (cleaned.length < RESET_CODE_LENGTH) return { kind: 'short', length: cleaned.length };
  if (cleaned.length > RESET_CODE_LENGTH) return { kind: 'long', length: cleaned.length };

  let value = 0n;
  for (const c of body) value = value * 32n + BigInt(DECODE.get(c)!);
  // The check symbol is compared raw, as the backend compares it.
  return CHECK_ALPHABET[Number(value % 37n)] === last ? null : { kind: 'typo' };
}
